import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  underwritingAgentsApi,
  UNDERWRITING_AGENTS_QUERY_KEY
} from "api/underwriting-agents/underwriting-agents";
import { UnderwritingAgent } from "api/underwriting-agents/types";
import { useUnderwritingAgentsEnabled } from "shared/app/UnderwritingAgents/underwritingAgentsFlag";
import { Template } from "types/landlord/verificationTemplate";
import { getErrorMessage } from "utils/error";
import { withoutUnderwritingAgents } from "./helpers";

const agentIdOf = (template?: Template) =>
  template?.underwriting_agent?.id ?? null;

const matchesSavedAgentSetup = (template: Template, savedTemplate: Template) =>
  agentIdOf(template) === agentIdOf(savedTemplate) &&
  Boolean(template.underwriting_agents_enabled) ===
    Boolean(savedTemplate.underwriting_agents_enabled);

const leftOutReason = (latest?: UnderwritingAgent) =>
  latest
    ? "it's turned off. Turn it on in Settings → Users and AI agents to add it."
    : "it no longer exists.";

/**
 * Sets a template's underwriting agent fields for a create or update request.
 * Pass `savedTemplate` for an update; without it the template is a copy.
 */
export const useUnderwritingAgentsPayload = () => {
  const isUnderwritingAgentsEnabled = useUnderwritingAgentsEnabled();
  const queryClient = useQueryClient();

  const fetchLatestAgents = async () => {
    try {
      return await queryClient.fetchQuery({
        queryKey: UNDERWRITING_AGENTS_QUERY_KEY,
        queryFn: underwritingAgentsApi.list
      });
    } catch (err) {
      throw new Error(
        `Couldn't check the underwriting agent: ${getErrorMessage(err)}`
      );
    }
  };

  return async (
    template: Template,
    savedTemplate?: Template
  ): Promise<Template> => {
    // Neither field sent: an update keeps the saved agent and switch (the API
    // drops a deleted agent on the way), a copy gets none.
    if (
      !isUnderwritingAgentsEnabled ||
      template.underwriting_agent === undefined ||
      (savedTemplate && matchesSavedAgentSetup(template, savedTemplate))
    )
      return withoutUnderwritingAgents(template);

    const picked = template.underwriting_agent;
    const latestAgents = picked ? await fetchLatestAgents() : [];
    const latest = latestAgents.find((agent) => agent.id === picked?.id);
    // The API refuses a deleted agent, and a turned-off one unless the saved version already has it.
    const isKept =
      latest !== undefined &&
      (latest.active || latest.id === agentIdOf(savedTemplate));

    if (picked && !isKept)
      toast.warning(
        `Underwriting agent "${picked.nickname}" was left out: ${leftOutReason(latest)}`
      );

    const agentId = isKept ? latest.id : null;

    return {
      ...withoutUnderwritingAgents(template),
      underwriting_agent_id: agentId,
      // It also refuses the switch on without an agent.
      underwriting_agents_enabled:
        Boolean(template.underwriting_agents_enabled) && agentId !== null
    };
  };
};
